import React, { useEffect, useRef, useState } from "react";
import { Navbar, Nav, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';

type TrackInfo = { label?: string; settings?: MediaTrackSettings };

export function MediaDiag() {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>(() => localStorage.getItem("preferredMicId") || undefined);
  const [err, setErr] = useState<string | undefined>();
  const [state, setState] = useState<{ readyState?: string; muted?: boolean }>({});
  const [trackInfo, setTrackInfo] = useState<TrackInfo | undefined>();
  const [acState, setAcState] = useState<AudioContextState | "uninitialized">("uninitialized");

  const audioRef = useRef<HTMLAudioElement>(null);
  const meterRef = useRef<HTMLDivElement>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const acRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const cleanup = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (acRef.current) {
      try { acRef.current.close(); } catch {}
      acRef.current = null;
      setAcState("uninitialized");
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch {} });
      streamRef.current = null;
    }
    if (audioRef.current) (audioRef.current as any).srcObject = null;
    if (meterRef.current) meterRef.current.style.width = "0%";
  };

  async function wakeAudioContext() {
    if (!acRef.current) return;
    if (acRef.current.state === "suspended") {
      try { await acRef.current.resume(); } catch {}
      setAcState(acRef.current.state);
    }
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      cleanup();
      setErr(undefined);
      setTrackInfo(undefined);
      setState({});

      try {
        if (deviceId) localStorage.setItem("preferredMicId", deviceId);
        else localStorage.removeItem("preferredMicId");

        const constraints: MediaStreamConstraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
          video: false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const [track] = stream.getAudioTracks();
        const settings = track.getSettings?.() || {};
        const label = (track as any).label || track.label;
        setTrackInfo({ label, settings });
        setState({ readyState: track.readyState, muted: (track as any).muted });

        // Audio context + analyser
        const AC = new (window.AudioContext || (window as any).webkitAudioContext)();
        acRef.current = AC;
        setAcState(AC.state);
        if (AC.state === "suspended") {
          try { await AC.resume(); } catch {}
          setAcState(AC.state);
        }

        const src = AC.createMediaStreamSource(stream);
        const analyser = AC.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.8;
        src.connect(analyser);

        const data = new Uint8Array(analyser.fftSize);
        const loop = () => {
          analyser.getByteTimeDomainData(data);
          // RMS amplitude around 128 baseline
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = data[i] - 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);       // ~0..~90
          const pct = Math.min(100, (rms / 50) * 100);    // scale to percentage
          if (meterRef.current) {
            const clamped = Math.max(pct, 2);            // keep at least 2% so it’s visible
            meterRef.current.style.width = clamped + "%";
          }
          rafRef.current = requestAnimationFrame(loop);
        };
        loop();

        if (audioRef.current) {
          audioRef.current.srcObject = stream;
          audioRef.current.muted = true;
          audioRef.current.play().catch(() => {});
        }

        const list = await navigator.mediaDevices.enumerateDevices();
        if (!mounted) return;
        setDevices(list.filter(d => d.kind === "audioinput"));
      } catch (e: any) {
        if (!mounted) return;
        setErr(e?.name ? `${e.name}: ${e.message}` : String(e));
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          if (!mounted) return;
          setDevices(list.filter(d => d.kind === "audioinput"));
        } catch {}
      }
    })();

    return () => { mounted = false; cleanup(); };
  }, [deviceId]);

  return (
    <>
      <Navbar style={{ backgroundColor: "var(--site-purple)" }} variant="dark" expand="lg" sticky="top">
        <Container>
          <Navbar.Brand style={{ fontSize: "16px" }}>Gateway To Access</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link as={Link} to="/login">Back to App</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div style={{ padding: 16, fontFamily: "system-ui" }}>
        <h3>Media Diagnostics</h3>

        <div><strong>Selected deviceId:</strong> <code>{deviceId || "(default)"}</code></div>
        <div><strong>Track state:</strong> {state.readyState || "(n/a)"} {state.muted ? "(muted)" : ""}</div>
        <div><strong>Active device label:</strong> {trackInfo?.label || "(unknown)"}</div>
        <div><strong>Resolved deviceId:</strong> <code>{trackInfo?.settings?.deviceId || "(unknown)"}</code></div>
        <div><strong>Sample rate:</strong> {trackInfo?.settings?.sampleRate ? `${trackInfo.settings.sampleRate} Hz` : "(unknown)"}</div>

        <div style={{ margin: "12px 0", background: "#e9ecef", height: 10, width: 320, borderRadius: 4, overflow: "hidden" }}>
          <div
            ref={meterRef}
            style={{
              height: "100%",
              width: 0,
              background: "linear-gradient(90deg, #4caf50, #8bc34a)",
              transition: "width 60ms linear",
            }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label htmlFor="mic-select" style={{ marginRight: 8 }}>Mic device:</label>
          <select
            id="mic-select"
            onChange={e => setDeviceId(e.target.value || undefined)}
            value={deviceId || ""}
            style={{ minWidth: 260 }}
          >
            <option value="">(System default)</option>
            {devices.map(d => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || d.deviceId}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={wakeAudioContext} className="btn btn-sm btn-outline-secondary">
            Wake audio
          </button>
          <small>AudioContext: {acRef.current ? acRef.current.state : acState}</small>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
          Tip: pick a <em>named</em> microphone (avoid “System default”) and watch the green bar to see if it moves.
        </div>

        <audio ref={audioRef} />
        {err && <pre style={{ color: "crimson", marginTop: 12 }}>{err}</pre>}
      </div>
    </>
  );
}
